// ═══════════════════════════════════════════════════════════════
// Audit Log — write/read change records to the activityLog table
// Fields: incidentId, module, action, actorName, actorEmail,
//         targetType, targetId, details (JSON string)
// ═══════════════════════════════════════════════════════════════

import { createRecord, queryRecords } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from '../store/authStore';

const F = FIELDS.activityLog;

export interface AuditEntry {
  id: number;
  incidentId: string;
  module: string;
  action: string;
  actorName: string;
  actorEmail: string;
  targetType: string;
  targetId: string;
  details: Record<string, unknown>;
  createdAt?: string;
}

// ── Write ──────────────────────────────────────────────────────

export async function logActivity(
  module: string,
  action: string,
  targetType: string,
  targetId: string | number,
  details: Record<string, unknown> | string = {},
): Promise<void> {
  try {
    const { user, activeIncident } = useAuthStore.getState();
    const detailsStr = typeof details === 'string'
      ? details.substring(0, 1000)
      : JSON.stringify(details).substring(0, 1000);
    await createRecord(TABLES.activityLog, {
      [F.incidentId]:  activeIncident?.id ? String(activeIncident.id) : '',
      [F.module]:      module,
      [F.action]:      action,
      [F.actorName]:   (user as any)?.fullName || (user as any)?.name || '',
      [F.actorEmail]:  (user as any)?.email || '',
      [F.targetType]:  targetType,
      [F.targetId]:    String(targetId),
      [F.details]:     detailsStr,
    });
  } catch {
    // Non-blocking — audit failures must never crash the caller
  }
}

// ── Read ───────────────────────────────────────────────────────

export async function readAuditLog(incidentId: string, limit = 300): Promise<AuditEntry[]> {
  try {
    const filters = incidentId
      ? [{ field: F.incidentId, operator: 'eq' as const, value: incidentId }]
      : [];
    const res = await queryRecords<Record<string, unknown>>(TABLES.activityLog, {
      limit,
      offset: 0,
      filters,
      sort: { field: 'created_at', direction: 'desc' as const },
    });
    return (res.records as any[]).map(r => {
      let details: Record<string, unknown> = {};
      try { details = JSON.parse(String(r[F.details] ?? '{}')); } catch { details = { raw: String(r[F.details] ?? '') }; }
      return {
        id:          r.record_id as number,
        incidentId:  String(r[F.incidentId]  ?? ''),
        module:      String(r[F.module]       ?? ''),
        action:      String(r[F.action]       ?? ''),
        actorName:   String(r[F.actorName]    ?? ''),
        actorEmail:  String(r[F.actorEmail]   ?? ''),
        targetType:  String(r[F.targetType]   ?? ''),
        targetId:    String(r[F.targetId]     ?? ''),
        details,
        createdAt:   String((r as any).created_at ?? ''),
      };
    });
  } catch {
    return [];
  }
}
